import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import type {
  Role,
  CreateRoleRequest,
  RoleListResponse,
  PermissionListResponse,
  UserRolesResponse,
  AssignRoleResponse,
  Permission,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

export const useListRoles = (skip = 0, limit = 10) => {
  return useQuery({
    queryKey: ['roles', skip, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());
      const { data } = await apiClient.get<RoleListResponse>(
        `/roles?${params.toString()}`
      );
      return data;
    },
  });
};

export const useGetRole = (roleId: string) => {
  return useQuery({
    queryKey: ['roles', roleId],
    queryFn: async () => {
      const { data } = await apiClient.get<Role>(`/roles/${roleId}`);
      return data;
    },
    enabled: !!roleId,
  });
};

export const useListPermissions = () => {
  return useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('skip', '0');
      params.append('limit', '1000');
      const { data } = await apiClient.get<PermissionListResponse>(
        `/roles/permissions?${params.toString()}`
      );
      return data.items;
    },
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: CreateRoleRequest) => {
      const { data } = await apiClient.post<Role>('/roles', payload);
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: `Role ${data.name} created successfully`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to create role',
        type: 'error',
      });
    },
  });
};

export const useSetRolePermissions = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: { roleId: string; permissionIds: string[] }) => {
      const { data } = await apiClient.put<Role>(`/roles/${payload.roleId}/permissions`, {
        permission_ids: payload.permissionIds,
      });
      return data;
    },
    onSuccess: (_, variables) => {
      addNotification({
        message: 'Role permissions updated successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles', variables.roleId] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to update role permissions',
        type: 'error',
      });
    },
  });
};

export const useUserRoles = (userId: string) => {
  return useQuery({
    queryKey: ['users', userId, 'roles'],
    queryFn: async () => {
      const { data } = await apiClient.get<UserRolesResponse>(
        `/users/${userId}/roles`
      );
      return data;
    },
    enabled: !!userId,
  });
};

export const useAssignRole = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: {
      userId: string;
      roleId: string;
    }) => {
      const { data } = await apiClient.post<AssignRoleResponse>(
        `/users/${payload.userId}/roles`,
        { role_id: payload.roleId }
      );
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'Role assigned successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to assign role',
        type: 'error',
      });
    },
  });
};

export const useRemoveRole = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: { userId: string; roleId: string }) => {
      const { data } = await apiClient.delete(
        `/users/${payload.userId}/roles/${payload.roleId}`
      );
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'Role removed successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to remove role',
        type: 'error',
      });
    },
  });
};
